import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { IoHeart, IoHeartOutline, IoCreateOutline, IoTrashOutline, IoOpenOutline } from "react-icons/io5";
import DojoShell, { RED } from "@/components/DojoShell";
import YouTubeEmbed from "@/components/YouTubeEmbed";
import RichTextEditor from "@/components/RichTextEditor";
import ImageUpload from "@/components/ImageUpload";
import { useAuth } from "@/context/AuthContext";
import { useApp } from "@/context/AppContext";
import { getBiblio, createBiblio, updateBiblio, deleteBiblio, listBiblioPortes } from "@/lib/api";

const PORTES = [
  { index: 1, title: "Porte du Feu" }, { index: 2, title: "Porte de la Vie" },
  { index: 3, title: "Porte des Masques" }, { index: 4, title: "Porte du Fil" },
  { index: 5, title: "Porte des Racines" }, { index: 6, title: "Porte du Courage" },
  { index: 7, title: "L'Appel" },
];
const TYPES = ["Fiche", "Template", "Exercice", "Méthode", "Guide", "Dictionnaire", "Dossier"];
const PLATFORMS = {
  "Google Slides": { color: "#f5b400", emoji: "📊" },
  "Canva": { color: "#00c4cc", emoji: "🎨" },
  "PDF": { color: "#e11d2a", emoji: "📄" },
  "DOCX": { color: "#2b579a", emoji: "📝" },
  "Genially": { color: "#7c3aed", emoji: "✨" },
  "Gamma": { color: "#a855f7", emoji: "▲" },
  "Lien": { color: "#2563eb", emoji: "🔗" },
};
const PLATFORM_KEYS = Object.keys(PLATFORMS);
const ytId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};
const inp = { width: "100%", padding: "10px 12px", border: "1.5px solid var(--border-c)", borderRadius: 10, fontSize: 14, fontFamily: "inherit", color: "var(--text-primary)", background: "var(--bg-secondary)", boxSizing: "border-box" };

export default function BiblioFiche() {
  const { id } = useParams();
  const isNew = id === "nouvelle";
  const nav = useNavigate();
  const { loading, isAuthenticated, isAdmin } = useAuth();
  const { isFavorite, toggleFavorite } = useApp();
  const [r, setR] = useState(isNew ? { porte_index: 1, title: "", type: "Fiche", description: "", body: "", cover: "", url: "", video_url: "", links: [], is_new: false } : null);
  const [editing, setEditing] = useState(isNew);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && !isAuthenticated) nav("/connexion", { replace: true, state: { from: { pathname: `/bibliotheque/${id}` } } });
  }, [loading, isAuthenticated, nav, id]);

  useEffect(() => {
    if (isNew) { if (!loading && !isAdmin) nav("/bibliotheque", { replace: true }); return; }
    setEditing(false);
    getBiblio(id).then((data) => { setR(data); setDraft(data); }).catch(() => toast.error("Fiche introuvable"));
  }, [id]); // eslint-disable-line

  const startEdit = () => { setDraft({ ...r }); setEditing(true); };
  const save = async () => {
    if (!draft.title?.trim()) return toast.error("Le titre est requis");
    setBusy(true);
    try {
      const links = (draft.links || []).filter((l) => l && l.url && l.url.trim());
      const payload = { porte_index: Number(draft.porte_index) || 1, title: draft.title, type: draft.type, description: draft.description, body: draft.body || "", cover: draft.cover || "", video_url: (draft.video_url || "").trim(), url: links[0]?.url || draft.url || "", links, is_new: !!draft.is_new, order: draft.order || 0 };
      const saved = isNew ? await createBiblio(payload) : await updateBiblio(id, payload);
      toast.success(isNew ? "Fiche créée" : "Fiche mise à jour");
      if (isNew) nav(`/bibliotheque/${saved.id}`, { replace: true });
      else { setR(saved); setEditing(false); }
    } catch { toast.error("Échec de l'enregistrement"); } finally { setBusy(false); }
  };
  const remove = async () => { if (!window.confirm("Supprimer cette fiche ?")) return; await deleteBiblio(id); toast.success("Fiche supprimée"); nav("/bibliotheque"); };

  if (!r) return <DojoShell crumbs={[{ label: "Bibliothèque", to: "/bibliotheque" }]}><p style={{ color: "var(--text-secondary)" }}>Chargement…</p></DojoShell>;

  const crumbs = [{ label: "Bibliothèque", to: "/bibliotheque" }, { label: isNew ? "Nouvelle fiche" : r.title }];

  if (editing) {
    const d = draft || r;
    return (
      <DojoShell crumbs={crumbs}>
        <div style={s.editWrap}>
          <h1 style={s.h1}>{isNew ? "Nouvelle fiche" : "Éditer la fiche"}</h1>
          <div style={s.field}><span style={s.lbl}>Titre</span><input style={inp} data-testid="fiche-title" value={d.title} onChange={(e) => setDraft({ ...d, title: e.target.value })} /></div>
          <div style={s.row}>
            <div style={s.field}><span style={s.lbl}>Porte</span>
              <select style={inp} data-testid="fiche-porte" value={d.porte_index} onChange={(e) => setDraft({ ...d, porte_index: Number(e.target.value) })}>
                {PORTES.map((p) => <option key={p.index} value={p.index}>{p.index}. {p.title}</option>)}
              </select>
            </div>
            <div style={s.field}><span style={s.lbl}>Type</span>
              <select style={inp} data-testid="fiche-type" value={d.type} onChange={(e) => setDraft({ ...d, type: e.target.value })}>
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div style={s.field}><span style={s.lbl}>Résumé (carte)</span><textarea style={{ ...inp, resize: "vertical" }} rows={2} data-testid="fiche-desc" value={d.description} onChange={(e) => setDraft({ ...d, description: e.target.value })} /></div>
          <div style={s.field}><ImageUpload label="Image de couverture" testid="fiche-cover" value={d.cover} onChange={(v) => setDraft({ ...d, cover: v })} /></div>

          <div style={s.field}>
            <span style={s.lbl}>Liens de la ressource (plusieurs possibles : Canva, Slides, PDF…)</span>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="fiche-links">
              {(d.links || []).map((lk, idx) => (
                <div key={idx} style={s.linkRow}>
                  <select style={{ ...inp, width: 150, flexShrink: 0 }} data-testid={`fiche-link-platform-${idx}`} value={lk.platform || "Lien"}
                    onChange={(e) => { const links = [...d.links]; links[idx] = { ...links[idx], platform: e.target.value }; setDraft({ ...d, links }); }}>
                    {PLATFORM_KEYS.map((pk) => <option key={pk} value={pk}>{PLATFORMS[pk].emoji} {pk}</option>)}
                  </select>
                  <input style={inp} data-testid={`fiche-link-url-${idx}`} value={lk.url || ""} placeholder="https://…"
                    onChange={(e) => { const links = [...d.links]; links[idx] = { ...links[idx], url: e.target.value }; setDraft({ ...d, links }); }} />
                  <button style={s.linkDel} data-testid={`fiche-link-del-${idx}`} onClick={() => setDraft({ ...d, links: d.links.filter((_, i) => i !== idx) })}><IoTrashOutline size={16} /></button>
                </div>
              ))}
              <button style={s.linkAdd} data-testid="fiche-link-add" onClick={() => setDraft({ ...d, links: [...(d.links || []), { platform: "Canva", url: "" }] })}>+ Ajouter un lien</button>
            </div>
          </div>
          <label style={s.check}><input type="checkbox" data-testid="fiche-new" checked={!!d.is_new} onChange={(e) => setDraft({ ...d, is_new: e.target.checked })} /> Marquer comme « Nouveau »</label>
          <div style={s.field}><span style={s.lbl}>Vidéo YouTube (optionnel)</span>
            <input style={inp} data-testid="fiche-video" value={d.video_url || ""} placeholder="https://www.youtube.com/watch?v=…" onChange={(e) => setDraft({ ...d, video_url: e.target.value })} />
            {d.video_url && ytId(d.video_url) && (
              <div style={{ ...s.videoWrap, marginTop: 10 }}><iframe style={s.videoFrame} src={`https://www.youtube.com/embed/${ytId(d.video_url)}`} title="preview" frameBorder="0" allowFullScreen /></div>
            )}
          </div>
          <div style={s.field}><span style={s.lbl}>Contenu de la fiche (intérieur)</span>
            <RichTextEditor value={d.body} onChange={(v) => setDraft({ ...d, body: v })} placeholder="Écris le contenu détaillé de la ressource…" />
          </div>
          <div style={s.actions}>
            <button style={s.cancel} onClick={() => isNew ? nav("/bibliotheque") : setEditing(false)} data-testid="fiche-cancel">Annuler</button>
            <button style={{ ...s.save, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={save} data-testid="fiche-save">{busy ? "Enregistrement…" : "Enregistrer"}</button>
          </div>
        </div>
      </DojoShell>
    );
  }

  const fav = isFavorite(r.id);
  return (
    <DojoShell crumbs={crumbs}>
      <div style={s.hero}>
        {r.cover && <><span style={{ ...s.coverBlur, backgroundImage: `url(${r.cover})` }} /><span style={{ ...s.coverImg, backgroundImage: `url(${r.cover})` }} /></>}
        <span style={s.heroScrim} />
        <span style={s.heroKanji}>{r.porte_kanji}</span>
        <div style={s.heroBody}>
          <span style={s.heroPorte}>{r.porte_title} · {r.type}</span>
          <h1 style={s.heroTitle}>{r.title}</h1>
        </div>
      </div>

      <div style={s.actionBar}>
        <button style={{ ...s.favBtn, ...(fav ? s.favOn : {}) }} data-testid="fiche-fav" onClick={() => toggleFavorite(r.id)}>
          {fav ? <IoHeart color={RED} size={18} /> : <IoHeartOutline size={18} />} {fav ? "Dans mes favoris" : "Ajouter aux favoris"}
        </button>
        {isAdmin && (
          <div style={s.adminBtns}>
            <button style={s.editBtn} data-testid="fiche-edit" onClick={startEdit}><IoCreateOutline size={16} /> Éditer</button>
            <button style={s.delBtn} data-testid="fiche-delete" onClick={remove}><IoTrashOutline size={16} /></button>
          </div>
        )}
      </div>

      {(() => {
        const links = (r.links && r.links.length ? r.links : (r.url && r.url !== "#" ? [{ platform: "Lien", url: r.url }] : []));
        if (!links.length) return <div style={s.soonWrap}><span style={s.soonPill} data-testid="fiche-soon">⏳ Ressource bientôt disponible</span></div>;
        return (
          <div style={s.portalsWrap} data-testid="fiche-portals">
            <div style={s.portalsLabel}>Accéder à la ressource</div>
            <div style={s.portalsGrid}>
              {links.map((lk, i) => {
                const meta = PLATFORMS[lk.platform] || PLATFORMS["Lien"];
                return (
                  <a key={i} style={{ ...s.portal, borderColor: `${meta.color}55` }} href={lk.url} target="_blank" rel="noreferrer" data-testid={`fiche-open-link-${i}`}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = `0 18px 40px ${meta.color}44`; e.currentTarget.style.borderColor = meta.color; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = `${meta.color}55`; }}>
                    <span style={{ ...s.portalGlow, background: `radial-gradient(circle at 70% 20%, ${meta.color}33, transparent 60%)` }} />
                    <span style={{ ...s.portalIcon, background: meta.color }}>{meta.emoji}</span>
                    <div style={{ flex: 1, minWidth: 0, position: "relative" }}>
                      <div style={s.portalName}>{lk.platform}</div>
                      <div style={s.portalSub}>Ouvrir le portail</div>
                    </div>
                    <IoOpenOutline size={20} color={meta.color} style={{ position: "relative" }} />
                  </a>
                );
              })}
            </div>
          </div>
        );
      })()}

      <article style={s.body} className="reader-rich" data-testid="fiche-body"
        dangerouslySetInnerHTML={{ __html: r.body || `<p>${r.description || ""}</p>` }} />

      {r.video_url && ytId(r.video_url) && (
        <div style={{ maxWidth: 760, marginTop: 20 }} data-testid="fiche-video-embed">
          <YouTubeEmbed url={r.video_url} title={r.title} />
        </div>
      )}
    </DojoShell>
  );
}

const s = {
  h1: { fontSize: 28, fontWeight: 900, margin: "0 0 16px" },
  editWrap: { maxWidth: 760 },
  field: { display: "flex", flexDirection: "column", gap: 6, marginBottom: 14, flex: 1 },
  lbl: { fontSize: 12.5, fontWeight: 700, color: "var(--text-secondary)" },
  row: { display: "flex", gap: 12 },
  check: { display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, fontWeight: 600, marginBottom: 14, cursor: "pointer", color: "var(--text-primary)" },
  actions: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 8 },
  cancel: { padding: "11px 18px", borderRadius: 10, border: "1.5px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-secondary)", fontWeight: 700, cursor: "pointer" },
  save: { padding: "11px 22px", borderRadius: 10, border: "none", background: RED, color: "#fff", fontWeight: 800, cursor: "pointer", boxShadow: `0 6px 16px color-mix(in srgb, var(--brand) 27%, transparent)` },
  hero: { position: "relative", height: 240, borderRadius: 20, overflow: "hidden", backgroundColor: "var(--bg-tertiary)", marginBottom: 16 },
  coverBlur: { position: "absolute", inset: 0, backgroundSize: "cover", backgroundPosition: "center", filter: "blur(22px)", transform: "scale(1.2)", opacity: 0.5 },
  coverImg: { position: "absolute", inset: 0, backgroundSize: "contain", backgroundRepeat: "no-repeat", backgroundPosition: "center" },
  soonPill: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13.5, fontWeight: 800, padding: "10px 16px", borderRadius: 999, background: "rgba(234,179,8,0.16)", color: "#a16207", border: "1px solid rgba(234,179,8,0.4)" },
  heroScrim: { position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0.15), rgba(0,0,0,0.72))" },
  heroKanji: { position: "absolute", right: 18, top: 6, fontSize: 120, fontWeight: 800, color: "rgba(255,255,255,0.16)" },
  heroBody: { position: "absolute", left: 24, right: 24, bottom: 20 },
  heroPorte: { color: RED, fontWeight: 800, fontSize: 13, background: "var(--bg-secondary)", padding: "4px 10px", borderRadius: 999 },
  heroTitle: { color: "#fff", fontSize: 32, fontWeight: 900, margin: "10px 0 0", textShadow: "0 2px 20px rgba(0,0,0,0.6)" },
  toolbar: { display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 22 },
  actionBar: { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 22 },
  adminBtns: { display: "flex", gap: 8, marginLeft: "auto" },
  favBtn: { display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 16px", borderRadius: 999, border: "1.5px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-primary)", fontWeight: 700, cursor: "pointer", fontSize: 13.5 },
  favOn: { borderColor: RED, color: RED, background: "color-mix(in srgb, var(--brand) 8%, transparent)" },
  soonWrap: { marginBottom: 24 },
  portalsWrap: { marginBottom: 26 },
  portalsLabel: { fontSize: 12.5, fontWeight: 900, letterSpacing: 1, textTransform: "uppercase", color: "var(--text-secondary)", marginBottom: 12 },
  portalsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 14 },
  portal: { position: "relative", overflow: "hidden", display: "flex", alignItems: "center", gap: 14, padding: "18px 18px", borderRadius: 18, border: "1.5px solid var(--border-c)", background: "var(--bg-secondary)", textDecoration: "none", cursor: "pointer", transition: "transform .2s ease, box-shadow .2s ease, border-color .2s ease" },
  portalGlow: { position: "absolute", inset: 0, pointerEvents: "none" },
  portalIcon: { width: 46, height: 46, borderRadius: 13, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0, position: "relative", boxShadow: "0 6px 16px rgba(0,0,0,0.18)" },
  portalName: { fontWeight: 900, fontSize: 16, color: "var(--text-primary)", fontFamily: "var(--font-display)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  portalSub: { fontSize: 12.5, color: "var(--text-secondary)", fontWeight: 600, marginTop: 2 },
  linkBtn: { display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 16px", borderRadius: 10, border: "none", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 13.5, textDecoration: "none" },
  linkRow: { display: "flex", gap: 8, alignItems: "center" },
  linkDel: { width: 42, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 10, border: "1px solid #f3d0d0", background: "var(--bg-secondary)", color: "#c0392b", cursor: "pointer" },
  linkAdd: { alignSelf: "flex-start", padding: "9px 14px", borderRadius: 10, border: "1.5px dashed var(--border-c)", background: "var(--bg-secondary)", color: RED, fontWeight: 700, fontSize: 13, cursor: "pointer" },
  editBtn: { display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 16px", borderRadius: 10, border: `1.5px solid ${RED}`, background: "var(--bg-secondary)", color: RED, fontWeight: 700, cursor: "pointer", fontSize: 13.5 },
  delBtn: { width: 42, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 10, border: "1px solid #f3d0d0", background: "var(--bg-secondary)", color: "#c0392b", cursor: "pointer" },
  body: { fontSize: 16, lineHeight: 1.8, color: "var(--text-primary)", maxWidth: 760 },
  videoWrap: { position: "relative", width: "100%", paddingTop: "56.25%", borderRadius: 14, overflow: "hidden", background: "#000" },
  videoFrame: { position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" },
};
